package com.evoapps.evoshift.data.repository

import com.evoapps.evoshift.data.dto.owner.GroupSettingsLinkDto
import com.evoapps.evoshift.data.dto.owner.GroupSettingsValuesDto
import com.evoapps.evoshift.data.dto.workplace.WorkplaceFinancialsDto
import com.evoapps.evoshift.data.dto.workplace.WorkplaceSettingsDto
import com.evoapps.evoshift.data.mapper.WorkplaceFinancialsMapper
import com.evoapps.evoshift.data.mapper.WorkplaceSettingsMapper
import com.evoapps.evoshift.data.remote.AppFirestore
import com.evoapps.evoshift.data.remote.CloudFunctionsClient
import com.evoapps.evoshift.data.remote.CloudFunctionsException
import com.evoapps.evoshift.domain.model.GroupSaveSummary
import com.evoapps.evoshift.domain.model.GroupSettings
import com.evoapps.evoshift.domain.model.GroupSettingsLink
import com.evoapps.evoshift.domain.model.GroupSettingsRefusedException
import com.evoapps.evoshift.domain.model.SettingsSection
import com.evoapps.evoshift.domain.model.SettingsValues
import com.evoapps.evoshift.domain.repository.GroupSettingsRepository
import kotlinx.coroutines.flow.Flow
import kotlinx.coroutines.flow.catch
import kotlinx.coroutines.flow.distinctUntilChanged
import kotlinx.coroutines.flow.map
import kotlinx.serialization.json.Json
import kotlinx.serialization.json.JsonElement
import kotlinx.serialization.json.JsonNull
import kotlinx.serialization.json.JsonObject
import kotlinx.serialization.json.JsonPrimitive
import kotlinx.serialization.json.buildJsonObject
import kotlinx.serialization.json.contentOrNull
import kotlinx.serialization.json.jsonArray
import kotlinx.serialization.json.jsonObject
import kotlinx.serialization.json.jsonPrimitive
import kotlinx.serialization.json.put
import kotlinx.serialization.json.putJsonObject
import kotlin.coroutines.cancellation.CancellationException

/**
 * SH-269: a workplace's link to its group (workplaces/{id}/group_settings/current) and the
 * group's values (workplace_groups/{id}, the owner's) are read here; every write is a callable of
 * functions/groupSettings.js. A section goes to the server as its stored fields
 * ([SettingsSection.paths]), taken from the same DTOs a workplace's own save writes, so the group's
 * values have exactly the workplace's layout.
 */
class GroupSettingsRepositoryImpl(
    private val firestore: AppFirestore,
    private val functions: CloudFunctionsClient,
    private val settingsMapper: WorkplaceSettingsMapper,
    private val financialsMapper: WorkplaceFinancialsMapper
) : GroupSettingsRepository {

    override fun observeLink(workplaceId: String): Flow<GroupSettingsLink?> =
        linkDoc(workplaceId).snapshots
            .map { snapshot ->
                if (!snapshot.exists) return@map null
                val head = snapshot.data<GroupSettingsLinkDto>()
                if (head.groupId.isBlank()) return@map null
                val values = runCatching { snapshot.data<GroupSettingsValuesDto>() }.getOrNull()
                GroupSettingsLink(
                    groupId = head.groupId,
                    groupName = head.groupName,
                    isDefault = head.isDefault,
                    workplaceCount = head.workplaceCount,
                    group = values?.let(::valuesOf),
                    inherited = head.inheritedSections.mapNotNullTo(mutableSetOf()) { SettingsSection.fromId(it) }
                )
            }
            // Not a manager here any more, or offline before the first read: no group settings shown.
            .catch { emit(null) }
            .distinctUntilChanged()

    override suspend fun getGroupSettings(groupId: String, workplaceIds: List<String>): Result<GroupSettings> = guarded {
        val snapshot = firestore.collection("workplace_groups").document(groupId).get()
        val values = if (snapshot.exists) runCatching { snapshot.data<GroupSettingsValuesDto>() }.getOrNull()?.let(::valuesOf) else null
        val following = mutableMapOf<SettingsSection, MutableList<String>>()
        val own = mutableMapOf<SettingsSection, MutableList<String>>()
        workplaceIds.forEach { wp ->
            val link = runCatching { linkDoc(wp).get() }.getOrNull()?.takeIf { it.exists }?.data<GroupSettingsLinkDto>()
            val inherited = link?.takeIf { it.groupId == groupId }?.inheritedSections.orEmpty()
            SettingsSection.entries.forEach { section ->
                (if (section.id in inherited) following else own).getOrPut(section) { mutableListOf() } += wp
            }
        }
        GroupSettings(groupId = groupId, values = values, following = following, own = own)
    }

    override suspend fun saveGroupSettings(
        groupId: String,
        sections: Set<SettingsSection>,
        values: SettingsValues,
        templatesFrom: String?
    ): Result<GroupSaveSummary> = guarded {
        val settings = json.encodeToJsonElement(WorkplaceSettingsDto.serializer(), settingsMapper.toDto(values.settings)).jsonObject
        val financials = values.financials?.let {
            json.encodeToJsonElement(WorkplaceFinancialsDto.serializer(), financialsMapper.toDto(it)).jsonObject
        }
        val result = functions.call("saveGroupSettings", buildJsonObject {
            put("groupId", groupId)
            putJsonObject("sections") {
                sections.forEach { section ->
                    val source = if (section.storage == SettingsSection.Storage.FINANCIALS) financials else settings
                    if (source != null) put(section.id, sectionValues(section, source))
                }
            }
            if (templatesFrom != null) put("templatesFrom", templatesFrom)
        })
        summaryOf(result)
    }

    override suspend fun setInheritance(workplaceId: String, sections: Map<SettingsSection, Boolean>): Result<Set<SettingsSection>> = guarded {
        val result = functions.call("setGroupInheritance", buildJsonObject {
            put("workplaceId", workplaceId)
            putJsonObject("sections") { sections.forEach { (section, follow) -> put(section.id, follow) } }
        })
        (result as? JsonObject)?.get("inheritedSections")?.jsonArray
            ?.mapNotNullTo(mutableSetOf()) { SettingsSection.fromId(it.jsonPrimitive.contentOrNull.orEmpty()) }
            ?: emptySet()
    }

    private fun linkDoc(workplaceId: String) =
        firestore.collection("workplaces").document(workplaceId).collection("group_settings").document("current")

    private fun valuesOf(dto: GroupSettingsValuesDto): SettingsValues? = dto.settings?.let { settings ->
        SettingsValues(settingsMapper.toDomain(settings), dto.financials?.let(financialsMapper::toDomain))
    }

    private fun summaryOf(result: JsonElement): GroupSaveSummary {
        val sections = (result as? JsonObject)?.get("sections") as? JsonObject
        val following = mutableMapOf<SettingsSection, List<String>>()
        val own = mutableMapOf<SettingsSection, List<String>>()
        sections?.forEach { (id, value) ->
            val section = SettingsSection.fromId(id) ?: return@forEach
            val entry = value as? JsonObject ?: return@forEach
            following[section] = entry["following"]?.jsonArray?.mapNotNull { it.jsonPrimitive.contentOrNull }.orEmpty()
            own[section] = entry["own"]?.jsonArray?.mapNotNull { it.jsonPrimitive.contentOrNull }.orEmpty()
        }
        return GroupSaveSummary(following, own)
    }

    private inline fun <T> guarded(block: () -> T): Result<T> = try {
        Result.success(block())
    } catch (e: CancellationException) {
        throw e
    } catch (e: CloudFunctionsException) {
        val reason = ((e.details as? JsonObject)?.get("reason") as? JsonPrimitive)?.contentOrNull
        Result.failure(GroupSettingsRefusedException.Reason.of(reason)?.let(::GroupSettingsRefusedException) ?: e)
    } catch (e: Exception) {
        Result.failure(e)
    }

    companion object {
        /** As Firestore stores them: every field, nulls included (a null removes it on the server). */
        private val json = Json { encodeDefaults = true; explicitNulls = true }

        /** [section]'s stored fields taken from [source] (a DTO as JSON), a missing one as null. */
        fun sectionValues(section: SettingsSection, source: JsonObject): JsonObject = buildJsonObject {
            section.paths.forEach { path -> put(path, at(source, path)) }
        }

        private fun at(source: JsonObject, path: String): JsonElement =
            path.split('.').fold<String, JsonElement?>(source) { node, key -> (node as? JsonObject)?.get(key) } ?: JsonNull
    }
}
